import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { KiArbeitet } from '@/components/ki/KiArbeitet'
import { apiErrorText } from '@/lib/api-error'
import { applyAccountSuggestions, discardAccountSuggestions, suggestAccounts, type AccountSuggestionSelection } from '../api/incoming-invoices.api'
import type { AccountSuggestion, IncomingFrame, IncomingInvoiceDetail, IncomingInvoiceLine, LedgerAccount } from '../types/incoming-invoices.types'
import { kontoText } from './Sachkonto'

/**
 * AP-44 7. — **Sachkonto-Vorschlag der KI** in der Erfassungsmaske. Der Vorschlag steht neben dem Konto
 * (an der Rechnung oder an der Position, das entscheidet der Server) und kommt erst mit „Übernehmen"
 * hinein. Jede Antwort ist die ganze Rechnung.
 */

/** Die Fehler der KI tragen (wie in der Lesung) keinen Text in den Sprachdateien; die übrigen kommen von dort. */
const FEHLER: Record<string, string> = {
  INCOMING_INVOICE_AI_CONSENT: 'Für den Vorschlag der KI fehlt die Einwilligung in der gültigen Fassung — sie steht in den Einstellungen der Eingangsrechnungen.',
  INCOMING_INVOICE_AI_OFF: 'Die KI ist für deinen Betrieb nicht eingeschaltet (Einstellungen → KI).',
  INCOMING_INVOICE_AI_RESULT_INVALID: 'Die KI hat nichts Brauchbares geantwortet. Bitte noch einmal versuchen.',
}
const fehlerText = (e: unknown, sonst: string): string => {
  const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code
  return (code && FEHLER[code]) ?? apiErrorText(e, sonst)
}

/** 7.2 — die KI wird nur gefragt, wo die Stammdaten nichts oder nur die Vorgabe des Mandanten geliefert haben. */
export const kommtInFrage = (z: Pick<IncomingInvoiceLine, 'lineType' | 'expenseAccountSource'>): boolean =>
  (z.lineType === 'ITEM' || z.lineType === 'CHARGE') && (!z.expenseAccountSource || z.expenseAccountSource === 'TENANT_DEFAULT')

/** 7.1, 7.3 — „Sachkonten vorschlagen" gibt es nur, wo es etwas vorzuschlagen gibt: was nicht eingerichtet ist, wird nicht angeboten. */
export const kannVorschlagen = (rechnung: IncomingInvoiceDetail, rahmen: IncomingFrame): boolean =>
  rahmen.aiCapture && rechnung.editable && !rechnung.defaultExpenseAccount && rahmen.ledgerAccounts.some((k) => k.isActive) && rechnung.lines.some(kommtInFrage)

/** Die Handgriffe am Vorschlag — eine Stelle für Kopf, Position und die Leiste über dem Raster. */
export function useKontovorschlag(rechnung: IncomingInvoiceDetail, onRechnung: (r: IncomingInvoiceDetail) => void, onFehler: (t: string | null) => void) {
  const [laeuft, setLaeuft] = useState(false)
  // Nur „vorschlagen" fragt die KI — Übernehmen und Verwerfen schreiben bloß.
  const [fragt, setFragt] = useState(false)
  const tue = async (was: () => Promise<IncomingInvoiceDetail>, sonst: string, ki = false): Promise<IncomingInvoiceDetail | null> => {
    try {
      setLaeuft(true)
      setFragt(ki)
      onFehler(null)
      const r = await was()
      onRechnung(r)
      return r
    } catch (e) {
      onFehler(fehlerText(e, sonst))
      return null
    } finally {
      setLaeuft(false)
      setFragt(false)
    }
  }
  return {
    laeuft,
    fragt,
    vorschlagen: () => tue(() => suggestAccounts(rechnung.id), 'Die Sachkonten ließen sich nicht vorschlagen.', true),
    uebernehmen: (auswahl?: AccountSuggestionSelection) => tue(() => applyAccountSuggestions(rechnung.id, auswahl), 'Der Vorschlag ließ sich nicht übernehmen.'),
    verwerfen: (auswahl?: AccountSuggestionSelection) => tue(() => discardAccountSuggestions(rechnung.id, auswahl), 'Der Vorschlag ließ sich nicht verwerfen.'),
  }
}

/**
 * Der Vorschlag am Sachkonto: Nummer und Bezeichnung, der Grund als Tooltip; unsichere gelb wie in der
 * Lesung. Zwei Handgriffe — „Übernehmen" und „Verwerfen".
 */
export function KontoVorschlag({
  vorschlag,
  konten,
  laeuft,
  onUebernehmen,
  onVerwerfen,
}: {
  vorschlag: AccountSuggestion
  konten: readonly LedgerAccount[]
  laeuft: boolean
  onUebernehmen: () => void
  onVerwerfen: () => void
}) {
  // Die Bezeichnung aus dem Stamm von heute; die vom Zeitpunkt des Vorschlags nur, wenn das Konto dort fehlt.
  const konto = konten.some((k) => k.number === vorschlag.account) ? kontoText(konten, vorschlag.account) : [vorschlag.account, vorschlag.name].filter(Boolean).join(' ')
  const grund = [vorschlag.reason, vorschlag.doubt ? `Unsicher: ${vorschlag.doubt}` : null].filter(Boolean).join(' — ')
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
      <span
        role="note"
        // Fließtext, kein Flex: In der schmalen Spalte des Kopfs bricht der Vorschlag wie ein Satz um, nicht Wort für Wort.
        className={`rounded px-1 text-foreground ${vorschlag.sure ? 'bg-sky-500/15' : 'bg-amber-500/15'}`}
        title={grund || undefined}
        aria-label={`Vorschlag der KI${vorschlag.sure ? '' : ', unsicher'}: Sachkonto ${konto}${grund ? ` — ${grund}` : ''}`}
      >
        <Sparkles size={11} aria-hidden className="mr-1 inline align-[-1px]" />
        KI: <span className="tabular-nums">{konto}</span>
        {!vorschlag.sure && <span className="text-muted-foreground"> · unsicher</span>}
      </span>
      {/*
        Der Druck nimmt den Fokus nicht vorweg (wie am „Zuklappen" des Zeilenblocks): Sonst klappt die Position
        beim Drücken ihre Zusatzzeilen auf, der Knopf rutscht darunter weg, und der Klick kommt nie an.
      */}
      <button type="button" className="underline hover:text-foreground disabled:opacity-50" disabled={laeuft} onMouseDown={(e) => e.preventDefault()} onClick={onUebernehmen}>
        Übernehmen
      </button>
      <button type="button" className="underline hover:text-foreground disabled:opacity-50" disabled={laeuft} onMouseDown={(e) => e.preventDefault()} onClick={onVerwerfen}>
        Verwerfen
      </button>
    </span>
  )
}

/**
 * Die Leiste über dem Raster der Positionen: „Sachkonten vorschlagen" und — bei Vorschlägen je Position —
 * „Sichere Vorschläge übernehmen (n)". Steht nur da, wenn eines von beiden etwas zu tun hat.
 */
export function KontenVorschlagen({
  rechnung,
  rahmen,
  onRechnung,
  onFehler,
}: {
  rechnung: IncomingInvoiceDetail
  rahmen: IncomingFrame
  onRechnung: (r: IncomingInvoiceDetail) => void
  onFehler: (t: string | null) => void
}) {
  const v = useKontovorschlag(rechnung, onRechnung, onFehler)
  // „Kein Vorschlag" sähe sonst aus wie „nichts passiert" — das eine, was die Rechnung nicht selbst sagt.
  const [ohneTreffer, setOhneTreffer] = useState(false)
  const offen = rechnung.lines.filter((z) => z.accountSuggestion)
  const sichere = offen.filter((z) => z.accountSuggestion?.sure).length
  const kann = kannVorschlagen(rechnung, rahmen)
  if (!rechnung.editable || !rahmen.aiCapture || (!kann && !offen.length)) return null

  const vorschlagen = async () => {
    setOhneTreffer(false)
    const r = await v.vorschlagen()
    if (r) setOhneTreffer(!r.accountSuggestion && !r.lines.some((z) => z.accountSuggestion))
  }
  // Wo die Vorschläge stehen, liest sich aus der Rechnung — auch nach dem Vorschlag von selbst (7.3).
  const n = offen.length
  const hinweis = rechnung.accountSuggestion
    ? 'Alle Positionen bekämen dasselbe Sachkonto — der Vorschlag der KI steht oben am Sachkonto der Rechnung.'
    : n
      ? `${n} ${n === 1 ? 'Position hat' : 'Positionen haben'} einen Vorschlag der KI — er steht unter der Position.`
      : ohneTreffer
        ? 'Die KI hat für keine Position ein passendes Sachkonto gefunden.'
        : null
  const knopf = 'inline-flex items-center gap-1 rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50'

  return (
    <div className="space-y-1 text-sm">
      <div className="flex flex-wrap items-center gap-1">
        {kann && (
          <button
            type="button"
            className={knopf}
            disabled={v.laeuft}
            // Der Name bleibt „Sachkonten vorschlagen" — ein `title` allein würde ihn für Vorlesehilfen ersetzen.
            aria-label="Sachkonten vorschlagen"
            title="Die KI schlägt ein Sachkonto für die Positionen vor, denen weder Artikel, Kategorie noch Lieferant eines geben"
            onClick={() => void vorschlagen()}
          >
            <Sparkles size={13} /> {v.fragt ? <KiArbeitet text="Die KI schlägt vor" /> : 'Sachkonten vorschlagen'}
          </button>
        )}
        {sichere > 0 && (
          <button type="button" className={knopf} disabled={v.laeuft} onClick={() => void v.uebernehmen()}>
            Sichere Vorschläge übernehmen ({sichere})
          </button>
        )}
        {n > 1 && (
          <button type="button" className={knopf} disabled={v.laeuft} onClick={() => void v.verwerfen()}>
            Alle verwerfen ({n})
          </button>
        )}
      </div>
      {hinweis && (
        <p className="text-xs text-muted-foreground" role="status">
          {hinweis}
        </p>
      )}
    </div>
  )
}
